import * as THREE from './three'

// Vertical angle covered by a HUD panel: most of the Quest view, still fully visible.
export const XR_HUD_ANGLE = THREE.MathUtils.degToRad(70)
// Above the world, below the XR pointer beam/cursor (renderOrder 1000).
const XR_HUD_RENDER_ORDER = 900
// Distance of surfaces that size themselves (HTML bridge); popups sit closer.
export const XR_HUD_DISTANCE = 1

const raycaster = new THREE.Raycaster()
const e1 = new THREE.Euler(0, 0, 0, 'YXZ')

/**
 * World-anchored UI layer for immersive sessions.
 * Each panel captures the player's viewing pose when opened and stays there.
 * Panels bypass scene depth and the world octree; the pointer tests them first.
 *
 * Surfaces (HTML bridge) provide their own mesh sized in metres for
 * `XR_HUD_DISTANCE` and are placed by `yaw`/`pitch` (radians, + = left/up).
 * The nearest panel wins the raycast and is drawn on top (`layer`).
 */
export class XRHud {
  constructor(world) {
    this.world = world
    this.group = new THREE.Group()
    this.cameraGroup = new THREE.Group()
    this.panels = new Map()
  }

  show(panel, { angle = XR_HUD_ANGLE, yaw = 0, pitch = 0, layer = 0, distance: fixedDistance, followCamera = false } = {}) {
    const position = new THREE.Vector3()
    const orientation = new THREE.Quaternion()
    this.world.camera.getWorldPosition(position)
    this.world.camera.getWorldQuaternion(orientation)
    // Keep panels upright; looking up/down must not tilt the reading surface.
    const facing = new THREE.Euler().setFromQuaternion(orientation, 'YXZ')
    orientation.setFromEuler(new THREE.Euler(0, facing.y, 0, 'YXZ'))
    this.panels.set(panel, { angle, yaw, pitch, layer, position, orientation, fixedDistance, followCamera })
    this.sync()
    return () => this.hide(panel)
  }

  hide(panel) {
    if (!this.panels.delete(panel)) return
    if (panel.mesh && (panel.mesh.parent === this.group || panel.mesh.parent === this.cameraGroup)) panel.mesh.removeFromParent()
    this.sync()
  }

  // Called every XR frame: a ui rebuild recreates its mesh in the scene.
  sync() {
    if (!this.panels.size) {
      this.group.removeFromParent()
      this.cameraGroup.removeFromParent()
      return
    }
    if (this.group.parent !== this.world.stage.scene) this.world.stage.scene.add(this.group)
    if (Array.from(this.panels.values()).some(value => value.followCamera)) {
      if (this.cameraGroup.parent !== this.world.camera) this.world.camera.add(this.cameraGroup)
    } else this.cameraGroup.removeFromParent()
    for (const [panel, { angle, yaw, pitch, layer, position, orientation, fixedDistance, followCamera }] of this.panels) {
      const mesh = panel.mesh
      if (!mesh) continue
      if (panel.sItem) {
        this.world.stage.octree.remove(panel.sItem)
        panel.sItem = null
      }
      const parent = followCamera ? this.cameraGroup : this.group
      if (mesh.parent !== parent) {
        parent.add(mesh)
        mesh.matrixAutoUpdate = true
        mesh.matrixWorldAutoUpdate = true
        mesh.frustumCulled = false
        mesh.renderOrder = XR_HUD_RENDER_ORDER + layer
        mesh.material.transparent = true
        mesh.material.depthTest = false
        mesh.material.depthWrite = false
        mesh.material.needsUpdate = true
      }
      // Nodes fill `angle` of the view; surfaces keep their own size at a fixed distance.
      const distance = fixedDistance ?? (panel.isHudSurface
        ? XR_HUD_DISTANCE * (1 - 0.1 * layer)
        : (panel._height * panel._size) / (2 * Math.tan(angle / 2)))
      e1.set(pitch, yaw, 0)
      mesh.quaternion.setFromEuler(e1)
      mesh.position.set(0, 0, -distance).applyQuaternion(mesh.quaternion)
      if (!followCamera) {
        mesh.position.applyQuaternion(orientation).add(position)
        mesh.quaternion.premultiply(orientation)
      }
      mesh.scale.setScalar(panel.isHudSurface ? 1 - 0.1 * layer : 1)
    }
    this.group.updateMatrixWorld(true)
    this.cameraGroup.updateWorldMatrix(true, true)
  }

  // Mesh matrices come from the last XR render, the same pose the user saw.
  raycast(origin, direction, far) {
    if (!this.panels.size) return null
    const meshes = []
    for (const panel of this.panels.keys()) if (panel.mesh?.visible && (panel.mesh.parent === this.group || panel.mesh.parent === this.cameraGroup)) meshes.push(panel.mesh)
    if (!meshes.length) return null
    raycaster.set(origin, direction)
    raycaster.near = 0
    raycaster.far = far
    const hit = raycaster.intersectObjects(meshes, false)[0]
    if (!hit) return null
    for (const panel of this.panels.keys()) {
      if (panel.mesh === hit.object) return { node: panel, point: hit.point, distance: hit.distance, uv: hit.uv }
    }
    return null
  }

  clear() {
    for (const panel of Array.from(this.panels.keys())) this.hide(panel)
  }
}
